/**
 * Outsourcing → To send, pallet first (Mike, 2026-10-28): the floor checks a pallet against its paper, so the
 * board lists each order's pallets, then the parts on each pallet with ONE total, then the jobs that make it up.
 * Pieces cut on another order's job (a share, `_share`) are just one more job line under the pallet they ride.
 *
 * Ticking a pallet ticks the order's own tasks on it (task mode): jobq-api papers what the packing plan packed on
 * those pallets and adds the other orders' pieces riding them (`_task_mode_pallet_portions` / riders). A task
 * papers its pieces on EVERY pallet it is packed on, so pallets that share a task are ticked together
 * (`palletClosure`). A pallet with no own task left to tick holds only other orders' pieces: it gets its own
 * paper from the pallet button (pallet mode).
 */

/** The pallet's own order (whose plan it is on), not the order whose pieces sit on it. */
export function palletOrderOf(pl) {
  return {
    sales_order_id: pl.pallet_sales_order_id ?? pl.sales_order_id ?? null,
    sales_order_number: pl.pallet_sales_order_number ?? pl.sales_order_number ?? null,
  };
}

export function palletKeyOf(pl) {
  if (pl.pallet_id != null) return `id:${pl.pallet_id}`;
  if (pl.pallet_short_id) return `sid:${pl.pallet_short_id}`;
  return `num:${pl.pallet_num ?? '?'}`;
}

/** Lines of one part add up only when they are the same thing going to the same finish. */
function partKeyOf(t) {
  return [t.product_id ?? t.part_id, t.source_assembly_id ?? '', t.assembly_name ?? '',
    t.process_label ?? '', t.color_code ?? ''].join('|');
}

const num = (v) => Number(v || 0);

/**
 * group.tasks (home rows and `_share` rows, each with the pallets it is listed on) → pallets and loose tasks.
 * `isOwnSelectable(t)` = a task this order can tick. Returns
 *   { pallets: [{ key, pallet_id, short_id, pallet_num, palletOrder, total, ownTaskIds, riderOnly,
 *                 parts: [{ key, sample, total, lines: [{ t, qty, share }] }] }],
 *     loose: [tasks on no pallet] }
 * in the API's order (first appearance).
 */
/**
 * How many of a task's pieces on each of its pallets still have to go on a paper. Papers take packed pieces first
 * (jobq-api `_task_mode_pallet_portions`: packed less what the order's papers carry), so what is already papered
 * comes off the task's pallets in order. A share counts what is left of it (`remaining`); an own task is capped
 * at `units_remaining` (extras that stay home are already out of it). Returns [{ pl, qty }] with qty > 0, plus
 * `extras` = pieces left to send that are on no pallet (the plan never placed them).
 */
export function unsentOnPallets(t) {
  const pls = (t.pallets || []).filter(Boolean);
  const packed = pls.reduce((n, pl) => n + num(pl.qty), 0);
  let left;
  if (t._share) {
    left = t._share.remaining != null ? num(t._share.remaining) : packed - num(t._share.sent);
  } else {
    left = packed - num(t.batched_qty);
    if (t.units_remaining != null) left = Math.min(left, num(t.units_remaining));
  }
  left = Math.max(0, Math.min(left, packed));
  // what is papered comes off the first pallets
  let papered = packed - left;
  const out = [];
  pls.forEach((pl) => {
    const q = num(pl.qty);
    const off = Math.min(q, papered);
    papered -= off;
    if (q - off > 1e-9) out.push({ pl, qty: Math.round((q - off) * 1000) / 1000 });
  });
  const remaining = t._share ? left : (t.units_remaining != null ? num(t.units_remaining) : left);
  return { lines: out, packed, extras: Math.max(0, Math.round((remaining - left) * 1000) / 1000) };
}

/**
 * group.tasks (home rows and `_share` rows, each with the pallets it is listed on) → pallets and loose tasks.
 * `isOwnSelectable(t)` = a task this order can tick. Returns
 *   { pallets: [{ key, pallet_id, short_id, pallet_num, palletOrder, total, ownTaskIds, riderOnly,
 *                 parts: [{ key, sample, total, lines: [{ t, qty, packed, extras, share }] }] }],
 *     loose: [tasks with nothing unsent on a pallet] }
 * in the API's order (first appearance). A line's qty is what still goes on a paper from that pallet; `packed`
 * is what the plan put there.
 */
export function palletBuckets(tasks, isOwnSelectable) {
  const pallets = new Map();
  const loose = [];
  (tasks || []).forEach((t) => {
    const { lines, extras } = unsentOnPallets(t);
    if (!lines.length) {
      loose.push(t);
      return;
    }
    lines.forEach(({ pl, qty }, i) => {
      const key = palletKeyOf(pl);
      if (!pallets.has(key)) {
        pallets.set(key, {
          key,
          pallet_id: pl.pallet_id ?? null,
          short_id: pl.pallet_short_id || (pl.pallet_num != null ? `P${pl.pallet_num}` : 'Pallet'),
          pallet_num: pl.pallet_num ?? null,
          palletOrder: palletOrderOf(pl),
          total: 0,
          ownTaskIds: [],
          parts: new Map(),
        });
      }
      const p = pallets.get(key);
      const pk = partKeyOf(t);
      if (!p.parts.has(pk)) p.parts.set(pk, { key: pk, sample: t, total: 0, lines: [] });
      const part = p.parts.get(pk);
      // the extras are named once, on the task's first pallet line
      part.lines.push({ t, qty, packed: num(pl.qty), extras: i === 0 ? extras : 0, share: t._share || null });
      part.total += qty;
      p.total += qty;
      if (!t._share && isOwnSelectable(t) && !p.ownTaskIds.includes(t.task_id)) p.ownTaskIds.push(t.task_id);
    });
  });
  return {
    pallets: [...pallets.values()].map((p) => ({
      ...p,
      total: Math.round(p.total * 1000) / 1000,
      parts: [...p.parts.values()].map((x) => ({ ...x, total: Math.round(x.total * 1000) / 1000 })),
      riderOnly: p.ownTaskIds.length === 0,
    })),
    loose,
  };
}

/** The pallets that go on one paper with `key` (they share an own task) and those tasks. */
export function palletClosure(pallets, key) {
  const byKey = new Map(pallets.map((p) => [p.key, p]));
  const keys = new Set();
  const taskIds = new Set();
  const todo = [key];
  while (todo.length) {
    const k = todo.pop();
    if (keys.has(k) || !byKey.has(k)) continue;
    keys.add(k);
    byKey.get(k).ownTaskIds.forEach((id) => {
      if (taskIds.has(id)) return;
      taskIds.add(id);
      pallets.forEach((p) => { if (!keys.has(p.key) && p.ownTaskIds.includes(id)) todo.push(p.key); });
    });
  }
  return { keys: [...keys], taskIds: [...taskIds] };
}

/** 'all' | 'some' | 'none' of a pallet's own tasks ticked. */
export function palletTickState(pallet, selectedTaskIds) {
  const ids = pallet.ownTaskIds;
  const n = ids.filter((id) => selectedTaskIds.has(id)).length;
  if (!ids.length || n === 0) return 'none';
  return n === ids.length ? 'all' : 'some';
}

/** The earliest due date / staged time across a part's lines (ISO strings compare as text). */
export function earliest(values) {
  const v = values.filter(Boolean).sort();
  return v.length ? v[0] : null;
}
